import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Autocomplete,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { Send, Paperclip, X, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  sendEmailWithAttachments,
  fetchSignature,
  suggestContacts,
} from "../../../../../redux/actions/inboxAction";
import { enqueueSnackbar } from "notistack";

const ComposeEmail = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { signature, contacts } = useSelector((state) => state.inbox);

  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sending, setSending] = useState(false);
  const [contactQuery, setContactQuery] = useState("");

  useEffect(() => {
    dispatch(fetchSignature());
  }, [dispatch]);

  useEffect(() => {
    if (contactQuery.length >= 2) {
      dispatch(suggestContacts(contactQuery));
    }
  }, [contactQuery, dispatch]);

  const contactOptions = (contacts || []).map((c) => ({
    label: c.name ? `${c.name} <${c.email}>` : c.email,
    email: c.email,
  }));

  const handleFileSelect = (e) => {
    setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const handleSend = async () => {
    if (to.length === 0) {
      enqueueSnackbar(t("inbox_at_least_one_recipient"), { variant: "warning" });
      return;
    }
    if (!subject.trim()) {
      enqueueSnackbar(t("inbox_subject_required"), { variant: "warning" });
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("to", to.map((t) => t.email || t).join(","));
      if (cc.length > 0) formData.append("cc", cc.map((c) => c.email || c).join(","));
      if (bcc.length > 0) formData.append("bcc", bcc.map((b) => b.email || b).join(","));
      formData.append("subject", subject);

      let htmlBody = body.replace(/\n/g, "<br>");
      formData.append("body_html", htmlBody);
      attachments.forEach((file) => formData.append("attachments", file));

      await dispatch(sendEmailWithAttachments(formData));
      enqueueSnackbar(t("inbox_email_sent"), { variant: "success" });
      navigate("/admin/inbox/sent");
    } catch (err) {
      enqueueSnackbar(err.message || t("inbox_send_error"), { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const renderEmailField = (label, value, onChange) => (
    <Autocomplete
      multiple
      freeSolo
      options={contactOptions}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label || o.email || "")}
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setContactQuery(v)}
      renderTags={(val, getTagProps) =>
        val.map((opt, idx) => (
          <Chip
            {...getTagProps({ index: idx })}
            key={idx}
            label={opt.email || opt}
            size="small"
            sx={{ height: 24, fontSize: "0.7rem" }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label}
          size="small"
          sx={{ "& input": { fontSize: "0.85rem" } }}
        />
      )}
      size="small"
    />
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`, borderRadius: "12px",
          flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem" }}>
              {t("inbox_new_message")}
            </Typography>
            {!showCcBcc && (
              <Button
                size="small" variant="text"
                onClick={() => setShowCcBcc(true)}
                sx={{ textTransform: "none", fontSize: "0.75rem", color: theme.palette.text.secondary }}
              >
                {t("inbox_cc_bcc")}
              </Button>
            )}
          </Box>

          {/* To */}
          {renderEmailField(t("inbox_to"), to, setTo)}

          {/* CC / BCC */}
          {showCcBcc && (
            <>
              {renderEmailField(t("inbox_cc"), cc, setCc)}
              {renderEmailField(t("inbox_bcc"), bcc, setBcc)}
            </>
          )}

          {/* Subject */}
          <TextField
            fullWidth size="small" placeholder={t("inbox_subject")}
            value={subject} onChange={(e) => setSubject(e.target.value)}
            sx={{ "& input": { fontSize: "0.85rem" } }}
          />

          {/* Body */}
          <TextField
            fullWidth multiline
            minRows={10}
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={t("inbox_body")}
            sx={{ flex: 1, "& .MuiOutlinedInput-root": { fontSize: "0.85rem" } }}
          />

          {/* Signature preview */}
          {includeSignature && signature?.signature_html && (
            <Box
              sx={{
                p: 1.5, borderRadius: "8px",
                bgcolor: alpha(theme.palette.background.default, 0.5),
                border: `1px dashed ${theme.palette.divider}`,
                fontSize: "0.8rem",
              }}
              dangerouslySetInnerHTML={{ __html: signature.signature_html }}
            />
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {attachments.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 1, py: 0.5, borderRadius: "6px",
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <FileText size={12} color={theme.palette.text.secondary} />
                  <Typography variant="caption" sx={{ flex: 1, fontSize: "0.7rem" }} noWrap>{file.name}</Typography>
                  <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled }}>{formatSize(file.size)}</Typography>
                  <IconButton size="small" onClick={() => removeAttachment(idx)} sx={{ p: 0.25 }}><X size={12} /></IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <input type="file" multiple ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
              <Button
                size="small" variant="text"
                startIcon={<Paperclip size={14} />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none", fontSize: "0.75rem", color: theme.palette.text.secondary }}
              >
                {t("inbox_attach")}
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSignature}
                    onChange={(e) => setIncludeSignature(e.target.checked)}
                    size="small"
                    sx={{ p: 0.25 }}
                  />
                }
                label={<Typography sx={{ fontSize: "0.75rem" }}>{t("inbox_signature")}</Typography>}
                sx={{ mx: 0 }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
              onClick={handleSend}
              disabled={sending}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
            >
              {t("inbox_send")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ComposeEmail;
